import { useEffect, useRef, useState } from 'react';
import { services } from '../data/services';
import { Button } from './UI';

function ServicesMenu({ active }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <span className="nav-drop" ref={ref}>
      <button
        type="button"
        className={`nav-link ${active ? 'active' : ''}`.trim()}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        Services <span className="nav-caret" aria-hidden="true">{open ? '▴' : '▾'}</span>
      </button>
      {open ? (
        <div className="nav-pop" role="menu">
          {services.map((service) => (
            <a key={service.id} role="menuitem" href={`/services/${service.id}/`}>
              <span>{service.name}</span><span className="pop-glyph" aria-hidden="true">→</span>
            </a>
          ))}
          <hr />
          <a role="menuitem" href="/services/">
            <span>All services</span><span className="pop-glyph" aria-hidden="true">→</span>
          </a>
        </div>
      ) : null}
    </span>
  );
}

export function SiteHeader({ active }) {
  return (
    <header className="site-header">
      <div className="site-wrap site-header-inner">
        <a href="/" className="logo-link" aria-label="Byte-Size home">
          <img className="site-logo" src="/assets/logo-lockup-paper.svg" alt="Byte-Size" width="161" height="32" />
        </a>
        <nav className="site-nav" aria-label="Main navigation">
          <a className={`nav-link ${active === 'home' ? 'active' : ''}`.trim()} href="/">Home</a>
          <ServicesMenu active={active === 'services'} />
          <a className={`nav-link ${active === 'pricing' ? 'active' : ''}`.trim()} href="/pricing/">Pricing</a>
          <a className={`nav-link ${active === 'about' ? 'active' : ''}`.trim()} href="/about/">About</a>
        </nav>
        <Button as="a" href="/start/" size="sm">Tell us the problem</Button>
      </div>
    </header>
  );
}

export function ProofList() {
  return (
    <ul className="proof-list">
      <li><span className="proof-glyph" aria-hidden="true">✓</span><span>Free initial look and clear next step</span></li>
      <li><span className="proof-glyph" aria-hidden="true">✓</span><span>Plain-English answers, without the run-around</span></li>
      <li><span className="proof-glyph" aria-hidden="true">✓</span><span>No hidden call-out or surprise charges</span></li>
    </ul>
  );
}

export function CTABand({ heading, sub = 'Plain English is perfect.' }) {
  return (
    <section className="cta-band">
      <div className="site-wrap cta-band-inner">
        <div>
          <h2>{heading}</h2>
          <p>{sub}</p>
        </div>
        <Button as="a" href="/start/" iconRight="→">Tell us the problem</Button>
      </div>
    </section>
  );
}

export function SiteFooter() {
  return (
    <footer>
      <div className="site-footer-band">
        <div className="site-wrap site-footer-inner">
          <img className="footer-logo" src="/assets/logo-lockup-paper.svg" alt="Byte-Size" width="141" height="28" />
          <div className="footer-mid">
            <span>Friendly, local problem-solvers.</span>
            <span className="footer-muted">Based in the Sutherland Shire — local on-site help, remote anywhere.</span>
          </div>
          <div className="footer-right">
            <a href="/start/">Send us your problem →</a>
            <a className="footer-muted" href="/privacy/">Privacy</a>
            <span className="footer-muted">© 2026 Byte-Size</span>
          </div>
        </div>
      </div>
      <div className="registration-strip" aria-hidden="true" />
    </footer>
  );
}

export function PageShell({ active, children, cta }) {
  return (
    <>
      <SiteHeader active={active} />
      <main>{children}</main>
      {cta}
      <SiteFooter />
    </>
  );
}
